import { HistoryTerm, Suggestion, SuggestionTypes } from '~app/modules/generic/models';
import { Product } from '~shared/models';
import {
  PageNameTypes,
  ApplicationSearchEventSource,
  ApplicationSuggestionSelectedEventType
} from '~shared/modules/application-event/constants';
import {
  ProductClickApplicationEvent,
  SearchApplicationEvent,
  SuggestionSelectedApplicationEvent
} from '~shared/modules/application-event/models';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { ITEMS_TO_SHOW, PRODUCTS_TO_SHOW } from '~app/modules/generic/constants';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { BlackDealsFeatureFlagService } from '~shared/services/black-deals-feature-flag.service';
import { NavigationHelper } from '~shared/services/navigation-helper.service';

export class SuggestionApplicationEventsHandler {

  private suggestions: Suggestion[];
  private suggestedProducts: Product[];

  private get visibleSuggestionsCount(): number {
    return Math.min(ITEMS_TO_SHOW, this.suggestions.length);
  }

  private get visiblePromotionsCount(): number {
    return Math.min(PRODUCTS_TO_SHOW, this.suggestedProducts.length);
  }

  constructor(
    private applicationEventService: ApplicationEventService,
    private blackDealsFeatureFlagService: BlackDealsFeatureFlagService,
    private navigationHelper: NavigationHelper
  ) {
  }

  public setSuggestions(suggestions: Suggestion[]): void {
    this.suggestions = suggestions;
  }

  public setSuggestionsProducts(products: Product[]): void {
    this.suggestedProducts = products;
  }

  public fireSuggestionsSelected(suggestion: Suggestion | HistoryTerm, searchQuery): void {
    this.fireSuggestionTermSelected(suggestion, searchQuery);
  }

  public fireProductSelected(product: Product, priceType: PriceTypes, searchQuery: string): void {
    this.fireSuggestionProductSelected(product, searchQuery);
    this.fireProductClick(product, priceType);
  }

  public fireSuggestionAllProductsSelected(searchQuery: string): void {
    this.applicationEventService.publish(
      new SuggestionSelectedApplicationEvent(
        ApplicationSuggestionSelectedEventType.AllPromoSuggestionSelected,
        searchQuery,
        this.visibleSuggestionsCount,
        this.visiblePromotionsCount
      )
    );
  }

  private fireSuggestionTermSelected(suggestion: Suggestion | HistoryTerm, searchQuery: string): void {
    const suggestionType: SuggestionTypes = suggestion.type;

    if (suggestionType === SuggestionTypes.category) {
      this.fireSuggestionCategory(suggestion, searchQuery);
    } else {
      this.fireSuggestionText(suggestion, searchQuery);
    }
  }

  private fireSuggestionText(suggestion: Suggestion | HistoryTerm, searchQuery: string): void {
    const eventType = (suggestion as HistoryTerm).expiresAt
      ? ApplicationSuggestionSelectedEventType.HistorySuggestionSelected
      : ApplicationSuggestionSelectedEventType.TextSuggestionSelected;

    const selectedSuggestionIndex = this.suggestions.findIndex(
      s => s.value === suggestion.value
    );

    this.applicationEventService.publish(
      new SuggestionSelectedApplicationEvent(
        eventType,
        searchQuery,
        this.visibleSuggestionsCount,
        this.visiblePromotionsCount,
        suggestion.value,
        selectedSuggestionIndex
      )
    );
  }

  private fireSuggestionCategory(suggestion: Suggestion | HistoryTerm, searchQuery: string): void {
    this.applicationEventService.publish(
      new SuggestionSelectedApplicationEvent(
        ApplicationSuggestionSelectedEventType.CategorySuggestionSelected,
        searchQuery,
        this.visibleSuggestionsCount,
        this.visiblePromotionsCount,
        suggestion.value,
        null,
        null,
        null,
        suggestion.value
      )
    );
  }

  private fireSuggestionProductSelected(product: Product, searchQuery: string): void {
    this.applicationEventService.publish(
      new SuggestionSelectedApplicationEvent(
        ApplicationSuggestionSelectedEventType.PromoSuggestionSelected,
        searchQuery,
        this.visibleSuggestionsCount,
        this.visiblePromotionsCount,
        null,
        null,
        product.id,
        product.name
      )
    );
  }

  private fireProductClick(product: Product, priceType: PriceTypes): void {
    this.applicationEventService.publish(
      new ProductClickApplicationEvent(
        this.navigationHelper.getCurrentUrl(),
        product,
        priceType,
        PageNameTypes.SearchSuggestions,
        this.blackDealsFeatureFlagService.isBlackDealsEnabled()
      )
    );
  }
}
